import type { TProductStickyPanelInstance } from 'widgets/product/ProductStickyPanel';
import type { IAddToCartButton } from 'widgets/product/AddToCartStickyMixin';
import type { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import type { TProduct } from 'core/product/CoreProductDetail';
import { isMember } from 'core/utils';

/**
 * @description Core AddToCartStickyMixin implementation
 * @param AddToCartStickyMixin Base widget for extending
 * @returns Add to Cart Sticky mixin widget
 */
function CoreAddToCartStickyMixinClassCreator(AddToCartStickyMixin: import('widgets/product/AddToCartStickyMixin').TAddToCartStickyMixin) {
    /**
     * @category widgets
     * @subcategory product
     * @class CoreAddToCartStickyMixin
     * @augments AddToCartStickyMixin
     */
    class CoreAddToCartStickyMixin extends AddToCartStickyMixin {
        isActionBtnIntersecting = true;

        isSignInToBuyActive = false;

        /**
         * @description Returns widget preferences, including default values.
         * @returns Merged preferences object
         */
        prefs() {
            return {
                headerId: 'headercontent',
                footerId: 'footercontent',
                availabilityId: 'availability',
                stickyPanelSignInToBuyBtn: 'stickyPanelSignInToBuy',
                signInToBuyBtn: 'signInToBuyButton',
                isLoyaltyEnrollmentRequired: false,
                isMembersOnly: false,
                isAvailable: false,
                stickyAddToCartBtn: 'stickyAddToCartBtn',
                selectSizeLabel: '',
                addToCartLabel: '',
                ...super.prefs()
            };
        }

        isFooterInViewport() {
            const footer = document.getElementById(this.prefs().footerId);
            const isFooterInViewport = footer && footer.getBoundingClientRect().top <= window.innerHeight;

            return isFooterInViewport;
        }

        /**
         * @description Defines whether the product has to be bought through the "Sign In To Buy" flow instead of a regular "Add to Cart" one
         * @param product productModel
         * @returns True when the "Sign In To Buy" flow should be used
         */
        isSignInToBuyRequired(product?: TProduct): boolean {
            const prefs = this.prefs();
            const isAvailable = product ? product.available : prefs.isAvailable;
            const isMembersOnly = product ? product.membersOnly : prefs.isMembersOnly;

            return Boolean(isAvailable) && Boolean(isMembersOnly) && !isMember(prefs.isLoyaltyEnrollmentRequired);
        }

        /**
         * @description Toggles sticky panel action buttons to mirror the currently active PDP action button
         */
        toggleStickyPanelActionButtons() {
            const prefs = this.prefs();
            const isSignInToBuy = this.isSignInToBuyActive;

            this.stickyPanel?.getById(prefs.stickyAddToCartBtn, (addToCartBtn: TProcessButtonInstance) => {
                if (isSignInToBuy) {
                    addToCartBtn.hide();
                } else {
                    addToCartBtn.show();
                }
            });

            this.stickyPanel?.getById(prefs.stickyPanelSignInToBuyBtn, (signInToBuyBtn: TProcessButtonInstance) => {
                if (isSignInToBuy) {
                    signInToBuyBtn.show();
                } else {
                    signInToBuyBtn.hide();
                }
            });
        }

        /**
         * @description Returns the PDP action button element the sticky panel visibility is anchored to.
         * @returns Observed button element
         */
        getObservedActionBtnElement(): HTMLElement | undefined {
            if (this.isSignInToBuyActive) {
                return this.ref(this.prefs().signInToBuyBtn).get();
            }

            return this.getById<IAddToCartButton, HTMLElement | undefined>(this.prefs().addToCartBtn, addToCartBtn => addToCartBtn.ref('self').get());
        }

        /**
         * @description Attach IntersectionObserver to check if footer is in the user's viewport for hide sticky panel
         */
        initFooterIntersectionObserver() {
            const footer = document.getElementById(this.prefs().footerId);
            const actionBtnElement = this.getObservedActionBtnElement();
            const headerHeight = document.getElementById(this.prefs().headerId)?.offsetHeight ?? 0;

            if (!footer || !actionBtnElement) {
                return;
            }

            const footerObserver = new IntersectionObserver(
                ([entry]) => {
                    const previousPanelState = this.isStickyPanelShown;
                    const actionBtnDisabled = actionBtnElement.classList.contains('m-disabled');

                    if (entry.isIntersecting || this.isActionBtnIntersecting || actionBtnDisabled) {
                        this.isStickyPanelShown = false;
                    } else {
                        const isAlwaysVisible = this.stickyPanel?.config.isAlwaysVisible === true;
                        const hasScrolledPastActionBtn = actionBtnElement.getBoundingClientRect().top < headerHeight;

                        this.isStickyPanelShown = isAlwaysVisible || hasScrolledPastActionBtn;
                    }

                    if (this.isStickyPanelShown !== previousPanelState) {
                        this.stickyPanel?.togglePanel(this.isStickyPanelShown);
                    }
                },
                {
                    threshold: 0
                }
            );

            footerObserver.observe(footer);
        }

        /**
         * @description Attach IntersectionObserver to check if the main action button is in the user's viewport
         */
        initPanelIntersectionObserver() {
            const actionBtnElement = this.getObservedActionBtnElement();
            const headerHeight = document.getElementById(this.prefs().headerId)?.offsetHeight ?? 0;
            const bottomMargin = this.stickyPanel?.config.isAlwaysVisible === true ? '-0.1' : '0';

            if (!actionBtnElement) {
                return;
            }

            this.observer = new IntersectionObserver(
                ([entry]) => {
                    const wasStickyPanelShown = this.isStickyPanelShown;
                    const actionBtnDisabled = actionBtnElement.classList.contains('m-disabled');
                    const actionBtnTop = actionBtnElement.getBoundingClientRect().top;
                    const isFooterInViewport = this.isFooterInViewport();

                    this.isActionBtnIntersecting = entry.isIntersecting;

                    if (entry.isIntersecting || actionBtnDisabled || isFooterInViewport) {
                        this.isStickyPanelShown = false;
                    } else {
                        const isAlwaysVisible = this.stickyPanel?.config.isAlwaysVisible === true;
                        const hasScrolledPastActionBtn = actionBtnTop < headerHeight;

                        this.isStickyPanelShown = isAlwaysVisible || hasScrolledPastActionBtn;
                    }

                    if (this.isStickyPanelShown !== wasStickyPanelShown) {
                        this.stickyPanel?.togglePanel(this.isStickyPanelShown);
                    }
                },
                {
                    threshold: 0,
                    rootMargin: `-${headerHeight}px 0px ${bottomMargin}px 0px`
                }
            );

            this.observer.observe(actionBtnElement);
        }

        /**
         * @description Init IntersectionObserver for sticky panel and footer
         */
        initIntersectionObserver() {
            this.getById('productStickyPanel', (stickyPanel: TProductStickyPanelInstance) => {
                this.stickyPanel = stickyPanel;

                if (!stickyPanel) {
                    return;
                }

                this.isSignInToBuyActive = this.isSignInToBuyRequired();
                this.toggleStickyPanelActionButtons();

                this.initPanelIntersectionObserver();
                this.initFooterIntersectionObserver();
            });
        }

        /**
         * @description Opens the "Sign In To Buy" flow from the sticky panel.
         */
        signInToBuyByPanel() {
            this.callIfExists('handleSignInToBuyClick', this.ref(this.prefs().signInToBuyBtn));
        }

        /**
         * @description Render sticky panel blocks
         * @param product productModel
         */
        renderStickyPanel(product: TProduct) {
            if (this.stickyPanel) {
                this.isSignInToBuyActive = this.isSignInToBuyRequired(product);

                this.stickyPanel.ref('self').toggleClass(this.prefs().classesHidden, !product.available);
                this.toggleStickyPanelActionButtons();

                if (product.available) {
                    this.renderStickyPanelImage(product);
                    this.renderStickyPanelName(product.productName);
                    this.renderStickyPanelPrice(product);
                    this.syncStickyAddToCartLabel(product);
                }
            }
        }

        /**
         * @description Keeps the sticky panel "add to cart" button label in sync with the dynamic size CTA state.
         * @param product productModel
         */
        syncStickyAddToCartLabel(product: TProduct) {
            if (!this.stickyPanel) {
                return;
            }

            this.stickyPanel.getById(this.prefs().stickyAddToCartBtn, (stickyBtn: TProcessButtonInstance) => {
                const showSelectSize = this.callIfExists('shouldShowSelectSize', product) === true;

                stickyBtn.setText(
                    showSelectSize ? this.prefs().selectSizeLabel : this.prefs()[this.prefs().update ? 'updateLabel' : 'addToCartLabel']
                );
            });
        }
    }

    return CoreAddToCartStickyMixin;
}

export type TCoreAddToCartStickyMixin = ReturnType<typeof CoreAddToCartStickyMixinClassCreator>;

export type TCoreAddToCartStickyMixinInstance = InstanceType<TCoreAddToCartStickyMixin>;

export default CoreAddToCartStickyMixinClassCreator;
